import { useMemo } from "react";
import { sumBy } from "lodash";
import { content } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { LegendItem } from "./legend";
import { Serie } from ".";

export function getDataFromSvg(svg) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const svgXml = new XMLSerializer().serializeToString(svg);
    const image = new Image();
    image.src = "data:image/svg+xml;base64," + btoa(svgXml);
    image.onload = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      context.drawImage(image, 0, 0);
      resolve(context.getImageData(0, 0, width, height));
    };
  });
}

const getHexaValue = (value: number): string => {
  let hex = value.toString(16);
  if (hex.length === 1) {
    hex = "0" + hex;
  }
  return hex.charAt(0) === hex.charAt(1) ? hex.charAt(0) : hex;
};

export function onChartMouseOver({ offsetX, offsetY }, chartData) {
  const index = (offsetY * chartData.width + offsetX) * 4;
  const [red, green, blue] = chartData.data.slice(index, index + 4);
  return `${getHexaValue(red)}${getHexaValue(green)}${getHexaValue(blue)}`;
}

export function fetchStreamId(e, chartData, legendSeries: LegendItem[]) {
  const hex = onChartMouseOver(e, chartData);
  return hex !== "000" && hex !== "fff"
    ? (legendSeries.find((s: LegendItem) =>
        s.color.match(new RegExp(hex, "gi"))
      ).id as string | undefined)
    : null;
}

export const parseStreamData = (data, field: Facet, metric: Metric) => {
  const formatKey = (key) => key[0].toUpperCase() + key.substring(1);
  const allEntries = [];
  data.forEach(({ stats }) =>
    stats[field].forEach(
      ({ term }) =>
        !allEntries.includes(formatKey(term)) &&
        allEntries.push(formatKey(term))
    )
  );
  const generateDots = (dots) => {
    const entries = {};
    allEntries.forEach((entry) => (entries[entry] = 0));
    dots.forEach(
      ({ counts, term }) => (entries[formatKey(term)] = counts[metric])
    );
    return entries;
  };
  return data.map(({ stats }) => generateDots(stats[field]));
};

export const getColors = (data, facet) => {
  if (data.length < 1) {
    return [];
  }
  const allKeys = Object.keys(data[0]).map((key) => key.toLowerCase());
  const values = facet.values.filter((f) => allKeys.includes(f.id.toString()));
  return allKeys.map((key) => values.find((v) => v.id === key).color);
};

export function useTotal(total: number, legendSeries) {
  return useMemo(() => {
    if (total) {
      return total;
    } else {
      return sumBy(legendSeries, "count");
    }
  }, [legendSeries, total]);
}

export function useColors(
  hovered: string,
  colors: string[],
  keys: string[],
  theme: GlossyTheme
) {
  return useMemo(() => {
    if (!hovered) {
      return colors;
    }
    const hoveredColor = keys.indexOf(hovered);
    return colors.map((color, i) =>
      i === hoveredColor ? color : content({ theme }, "tertiary")
    );
  }, [hovered, keys, colors, theme]);
}

export const getLegendSeries = (
  series: Serie[],
  colors: string[]
): LegendItem[] => {
  const formattedSeries = {};
  series.forEach((serie) =>
    Object.entries(serie).forEach((s, i) => {
      if (s[0] in formattedSeries) {
        return (formattedSeries[s[0]].count += s[1]);
      }
      return (formattedSeries[s[0]] = {
        id: s[0],
        label: s[0],
        count: s[1],
        color: colors[i],
      });
    })
  );
  return Object.values(formattedSeries);
};

export function getCustomTheme(theme: GlossyTheme) {
  return {
    axis: {
      fontSize: "14px",
      tickColor: content({ theme }, "tertiary"),
      ticks: {
        line: { stroke: content({ theme }, "tertiary") },
        text: { fill: content({ theme }, "tertiary") },
      },
    },
    grid: {
      line: { stroke: content({ theme }, "tertiary") },
    },
  };
}
